import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TaskCheck, TaskLite } from '@aop/domain';
import { toCamel } from '../../lib/case';
import { useRealtime, useRows } from '../../lib/data';
import { supabase } from '../../lib/supabase';
import type { UserRow } from '../users/UsersPage';

/** Công việc theo tên trường của app cũ (ánh xạ từ cột CSDL). */
export interface Task extends TaskLite {
  id: string; title: string; status: string; priority: string; progress: number; due: string; start: string; group: string; desc: string; dept: string;
  assigneeId: string; createdById: string; createdBy: string; watcherIds: string[]; checks: TaskCheck[]; repeat: string; doneAt: string; createdAt: string; updatedAt: string;
}

export function normTask(r: Record<string, unknown>): Task {
  const c = toCamel<Record<string, unknown>>(r);
  return {
    id: String(c.id), title: String(c.title ?? ''), status: String(c.status ?? 'todo'), priority: String(c.priority ?? 'normal'), progress: Number(c.progress) || 0,
    due: String(c.due ?? ''), start: String(c.startDate ?? ''), group: String(c.groupName ?? ''), desc: String(c.description ?? ''), dept: String(c.dept ?? ''),
    assigneeId: String(c.assigneeId ?? ''), createdById: String(c.createdBy ?? ''), createdBy: String(c.createdByName ?? ''), watcherIds: (c.watcherIds as string[]) ?? [],
    checks: (c.checks as TaskCheck[]) ?? [], repeat: String(c.repeat ?? ''), doneAt: String(c.doneAt ?? ''), createdAt: String(c.createdAt ?? ''), updatedAt: String(c.updatedAt ?? ''),
  };
}

export function useTasks() {
  const q = useRows<Record<string, unknown>>('tasks');
  const data = useMemo(() => q.data?.map(normTask), [q.data]);
  return { ...q, data };
}

export const useProfiles = () => useRows<UserRow>('profiles', { order: 'username' });

export interface TaskComment { id: string; taskId: string; byId: string | null; byName: string; text: string; at: string }
export interface TaskLogRow { id: string; taskId: string; byName: string; text: string; at: string }

/** Trao đổi của một công việc (chỉ tải khi mở chi tiết). */
export function useTaskComments(taskId: string | null) {
  useRealtime('task_comments');
  return useQuery({
    queryKey: ['task_comments', taskId], enabled: !!taskId,
    queryFn: async (): Promise<TaskComment[]> => {
      const { data, error } = await supabase().from('task_comments').select('*').eq('task_id', taskId).order('at', { ascending: true });
      if (error) throw error;
      return (data as Record<string, unknown>[]).map(r => toCamel<TaskComment>(r));
    },
  });
}

/** Lịch sử thay đổi của một công việc. */
export function useTaskLog(taskId: string | null) {
  useRealtime('task_log');
  return useQuery({
    queryKey: ['task_log', taskId], enabled: !!taskId,
    queryFn: async (): Promise<TaskLogRow[]> => {
      const { data, error } = await supabase().from('task_log').select('*').eq('task_id', taskId).order('at', { ascending: true });
      if (error) throw error;
      return (data as Record<string, unknown>[]).map(r => toCamel<TaskLogRow>(r));
    },
  });
}

/** Số bình luận của từng công việc (hiện trên thẻ), chỉ tải cột task_id. */
export function useCommentCounts() {
  useRealtime('task_comments');
  return useQuery({
    queryKey: ['task_comments', 'counts'],
    queryFn: async (): Promise<Record<string, number>> => {
      const out: Record<string, number> = {};
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase().from('task_comments').select('task_id').order('id', { ascending: true }).range(from, from + 999);
        if (error) throw error;
        for (const r of data as { task_id: string }[]) out[r.task_id] = (out[r.task_id] ?? 0) + 1;
        if (!data || data.length < 1000) break;
      }
      return out;
    },
  });
}

export interface LinkedEvent { id: string; title: string; type: string; date: string; startTime: string }

/** Lịch gắn với một công việc. */
export function useTaskEvents(taskId: string | null) {
  useRealtime('events');
  return useQuery({
    queryKey: ['events', 'task', taskId], enabled: !!taskId,
    queryFn: async (): Promise<LinkedEvent[]> => {
      const { data, error } = await supabase().from('events').select('id,title,type,date,start_time').eq('task_id', taskId).order('date', { ascending: true });
      if (error) throw error;
      return (data as Record<string, unknown>[]).map(r => toCamel<LinkedEvent>(r));
    },
  });
}
